import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthenticationService,
  CartService,
  DataService,
  DocumentService
} from '../../services';
import { 
  FilterDetails, 
  MedicalTest, 
  User, 
  Search,
  CartQuantityItem
} from '../../models';
import { first } from 'rxjs/operators';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-all-medical-tests',
  templateUrl: './medical-tests.component.html',
  styleUrls: ['./medical-tests.component.scss']
})

export class MedicalTestsComponent implements OnInit, OnDestroy {
  itemsPerPage = 9;
  page: number = 1;
  filterIcons: FilterDetails[] = [];
  medicalTests: MedicalTest[] = [];
  user: User;
  cartItems: MedicalTest[] = [];
  categoryNames: string[] = [];
  isSelectedMedicalTest: boolean = false;
  isResponsive: boolean = true;
  stateOfFilter: boolean[] = [];
  slickCarouselConfig: any;
  medicalTestQuantity: { [productId: number]: number } = {}; 
  medicalTestQuantitiesAdded: CartQuantityItem[] = [];

  constructor(
    private router: Router,
    private cartService: CartService,
    private dataService: DataService,
    private documentService: DocumentService,
  ) {}

  ngOnInit() {
    this.slickCarouselConfig = {
      ...this.documentService.getslickCarouselConfig(),
      autoplay: true,
      autoplaySpeed: 3000,
      slidesToScroll: 3
    };
    this.populateMedicalTests();
    this.filterIcons = this.dataService.getLocalFilterDetails();
    this.cartService.getCartProducts().subscribe(cartItems => {
      this.cartItems = (!cartItems || cartItems.length === 0) ? 
        JSON.parse(localStorage.getItem('cartItems') || '[]') : cartItems as MedicalTest[];
    });
    this.dataService.getSearchCriteria('medicalTests').subscribe(searchCriteria => {
      if (searchCriteria) {
        this.filterMedicalTests(searchCriteria as Search); 
      }
    });
    this.dataService.getLocalCartItemQuantities().subscribe((quantities) => {
      this.medicalTestQuantitiesAdded = quantities;
      this.medicalTestQuantitiesAdded && this.medicalTestQuantitiesAdded.map(quantity => {
        this.medicalTestQuantity[quantity.productId] = quantity.quantity;
      });
    });
    this.adjustItemsPerPage(window.innerWidth);
  }

  ngOnDestroy(): void {
    this.dataService.saveLocalSearchCriteria(null, 'medicalTests');
  }

  populateMedicalTests() {
    this.dataService.getAllLocalMedicalTests().subscribe(
      medicalTests => {
        this.medicalTests = medicalTests;
        if (this.medicalTests.length === 0) {
          this.router.navigateByUrl('/');
        }
      }
    );
  }

  MedicalTestInCart(medicalTest: MedicalTest): boolean {
    // this.cartItems = JSON.parse(localStorage.getItem('cartItems') || '[]');
    return this.cartItems.some(item => item.id === medicalTest.id);
  }

  addToCart(medicalTest: MedicalTest) {
    const cartHasDrug = this.cartItems.some(item => ('country_of_origin' in item));
    const isInCart = this.MedicalTestInCart(medicalTest);
    if ( cartHasDrug && !isInCart) {
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: 'You can only add either a medical test or a drug to the cart at a time.',
        confirmButtonColor: '#f37b7b',
      });
      return;
    }
    if (isInCart) {
      this.cartService.removeFromCart(medicalTest);
      delete this.medicalTestQuantity[medicalTest.id];
      this.medicalTestQuantitiesAdded = this.medicalTestQuantitiesAdded.filter(
        item => item.productId !== medicalTest.id
      );
      this.dataService.removeMedicalTestFromLocalStorageCart(medicalTest.id);
    } else {
      this.dataService.addProductToCart(medicalTest);
      this.medicalTestQuantity[medicalTest.id] = 1;
      this.medicalTestQuantitiesAdded = this.medicalTestQuantitiesAdded || [];
      this.medicalTestQuantitiesAdded.push({
        productId: medicalTest.id,
        quantity: 1
      });
    }
    this.updateMedicalTestQuantities();
    this.refreshCartState(medicalTest.id);
  }

  refreshCartState(medicalTestId: number): void {
    const { cartItems, quantities } = this.dataService.refreshCartState();
    this.cartItems = cartItems.filter((item): item is MedicalTest => (item as MedicalTest).duration !== undefined);
    this.medicalTestQuantitiesAdded = quantities;
  }

  decreaseQuantity(medicalTest: MedicalTest) {
    if (this.medicalTestQuantity[medicalTest.id] > 1) {
      this.medicalTestQuantity[medicalTest.id] -= 1;
    } else {
      delete this.medicalTestQuantity[medicalTest.id];
      this.cartService.removeFromCart(medicalTest);
      this.dataService.removeMedicalTestFromLocalStorageCart(medicalTest.id);
    }
    this.updateMedicalTestQuantities();
  }

  increaseQuantity(medicalTest: MedicalTest) {
    if (!this.MedicalTestInCart(medicalTest)) {
      this.medicalTestQuantity[medicalTest.id] = 1;
      this.medicalTestQuantitiesAdded.push({
        productId: medicalTest.id,
        quantity: 1
      });
      this.dataService.addRemoveCartItem(medicalTest, this.cartItems);
    } else {
      this.medicalTestQuantity[medicalTest.id] += 1;
    }
    this.updateMedicalTestQuantities();
  }  

  updateMedicalTestQuantities() {
    this.medicalTestQuantitiesAdded = Object.entries(this.medicalTestQuantity).map(
      ([productId, quantity]) => ({
        productId: Number(productId),
        quantity: quantity as number,
      })
    );
    this.dataService.saveLocalCartItemQuantities([...this.medicalTestQuantitiesAdded]);
  }
  
  filterMedicalTests(searchCriteria: Search, index?: number) {
    this.dataService.filterMedicalTests(searchCriteria, index)
      .subscribe(response => this.medicalTests = response);
  }

  goToMedicalTestDetails(medicalTest: MedicalTest) {
    this.dataService.saveLocalDataToView(medicalTest);
    this.router.navigate(['medical-test-details']);
  }

  adjustItemsPerPage(width: number) {
    if (width >= 768 && width <= 1199) {
      this.itemsPerPage = 8; // Between 768px and 1200px → Show 8 items to balance the white space
    } else {
      this.itemsPerPage = 9;
    }
  }
}
